import dayjs, { type Dayjs, locale as dayjsLocale } from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration, { type DurationUnitType } from 'dayjs/plugin/duration';
import localizedFormatPlugin from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import weekday from 'dayjs/plugin/weekday';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import { isRef, type MaybeRef } from 'vue';
import { DayOfWeek, type Locale, type Maybe } from '@/types';
import { capitalizeFirstLetter, getObjectKeys } from '@/utils/helpers';

/**
 * DOCUMENTATION ET LIENS RAPIDES:
 * - Tokens de formatage: https://day.js.org/docs/en/parse/string-format
 * - Tokens de formatage localisées: https://day.js.org/docs/en/display/format#list-of-localized-formats
 * - Customisation du formatage par défaut de day.js: https://day.js.org/docs/en/customization/customization
 */

dayjs.extend(relativeTime);
dayjs.extend(calendar);
dayjs.extend(weekday);
dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormatPlugin);

interface FormatOptionsBase {
  locale?: MaybeRef<Locale>;
  destinationFormat?: string;
  currentFormat?: string;
}

const DEFAULT_TIME_FORMAT = 'HH:mm'; // Heure toujours exprimée au format 24h

/**
 * Liste de formats custom en fonction des langues supportées.
 *  Ceci évite de remplacer des formats par défaut définis dans day.js ou de définir un nouvel objet de formatage à
 * assigner à une nouvelle locale "custom".
 * Finalement, ça évite le chargement d'un énième plugin de day.js uniquement pour quelques formatages spécifiques.
 *
 * NOTE:  LE FORMAT À ÉVALUER DOIT ÊTRE TOUJOURS LA VERSION FRANÇAISE!
 */
const customLocalizedFormats = {
  'dddd D MMMM': {
    fr: 'dddd D MMMM',
    en: 'dddd, MMMM D',
  },
  'D MMMM': {
    fr: 'D MMMM',
    en: 'MMMM D',
  },
  'dddd D MMMM YYYY': {
    fr: 'dddd D MMMM YYYY',
    en: 'dddd, MMMM D, YYYY',
  },
  'D MMMM YYYY': {
    fr: 'D MMMM YYYY',
    en: 'MMMM D, YYYY',
  },
};

function isKeyofLocalizedFormat(str: unknown): str is keyof typeof customLocalizedFormats {
  return typeof str === 'string' && str in customLocalizedFormats;
}

/**
 * Retourne le format custom de date en fonction de la langue présentement sélectionnée.
 * Si le format n'existe pas dans l'objet plus haut, le format fourni est simplement retourné.
 */
function getLocalizedFormats(format: Maybe<string>): string | Record<Locale, string> {
  if (isKeyofLocalizedFormat(format)) {
    return customLocalizedFormats[format];
  }
  return format ?? '';
}

/**
 * Effectue une conversion d'un nombre fourni vers le type désiré afin
 * de recevoir le résultat final en millisecondes
 */
export const toMilliseconds = (duration: number, unitType?: DurationUnitType): number =>
  dayjs.duration(duration, unitType).asMilliseconds();

/** Formate une date sans l'année, par exemple 22 janvier ou January 22. */
export function formatDayOfYear(value: string): string {
  return formatDateTime(value, { destinationFormat: 'D MMMM', currentFormat: 'MM-DD' });
}

export function setDayjsLocale(newLocale: string): void {
  dayjs.locale(newLocale);
}

/**
 * Formate la date normalisée (YYYY-MM-DD) reçue selon la langue présentement active.
 */
export function formatDateLocalized(value: string | Date, format = 'LL'): string {
  const parsed = dayjs(value);
  const localizedFormat = getLocalizedFormats(format);
  const currentLocale = dayjsLocale() as Locale; // dayjs retourne une string pour sa locale

  return parsed.isValid()
    ? parsed.format(typeof localizedFormat === 'string' ? localizedFormat : localizedFormat[currentLocale])
    : value.toString();
}

export type DateLike = string | number | Date | Dayjs;

/**
 * Formatage en temps seulement d'une date exprimée en string ou une Date JavaScript.
 */
export function formatTime(dateValue: DateLike, options?: FormatOptionsBase): string {
  const destinationFormat = options?.destinationFormat || DEFAULT_TIME_FORMAT;
  return formatDateTime(dateValue, { ...options, destinationFormat });
}

/**
 * Transforme une date constituée dans un format non-normalisée (exemple: une date HOD au format "YYYYMMDD")
 * en un objet dayjs valide puis, lorsqu'un format "user-friendly" est fourni, applique ce format avant
 * de retourner le résultat.
 */
export function formatDateTime(value: DateLike, options?: FormatOptionsBase): string {
  const providedLocale = options?.locale
    ? isRef(options.locale)
      ? options.locale.value
      : options.locale
    : (dayjsLocale() as Locale);
  const parsed = dayjs(value, { format: options?.currentFormat, locale: providedLocale });
  const localizedFormat = getLocalizedFormats(options?.destinationFormat);
  return parsed.isValid()
    ? parsed.format(typeof localizedFormat === 'string' ? localizedFormat : localizedFormat[providedLocale])
    : value.toString();
}

/**
 * Retourne la date fournie sous le format "jour de la semaine, date et mois" (ex. Mardi 17 mai).
 * Le paramètre optionnel de la locale permet de retourner la valeur dans la langue désirée.
 */
export function formatDayOfMonth(value: string, locale?: MaybeRef<Locale>): string {
  return formatDateTime(value, { destinationFormat: 'dddd D MMMM', locale });
}

/**
 * Retourne le nom du jour en fonction de l'indice fourni en paramètres (0 à 6) ou la date fournie (YYYY-MM-DD),
 * ainsi que de la locale utilisée. Optionnellement, le résultat peut être retourné avec une majuscule au début,
 * ou tout en minuscules.
 */
export function getDayOfWeek(value: number | string, capitalize = true, options?: Pick<FormatOptionsBase, 'locale'>): string {
  const providedLocale = options?.locale
    ? isRef(options.locale)
      ? options.locale.value
      : options.locale
    : (dayjsLocale() as Locale);

  const output = typeof value === 'number'
    ? dayjs().day(value).locale(providedLocale).format('dddd')
    : dayjs(value).locale(providedLocale).format('dddd');
  return capitalize ? capitalizeFirstLetter(output) : output.toLowerCase();
}

/**
 * Retourne la valeur du jour suivant selon l'enum `DayOfWeek` en fonction du libellé fourni en paramètre
 *
 * @examples
 * getNextDayByName(DayOfWeek.Friday) // 'saturday'
 */
export function getNextDayByName(label: DayOfWeek): DayOfWeek {
  const daysOfWeekKeys = getObjectKeys(DayOfWeek);
  const idx = daysOfWeekKeys.findIndex(day => DayOfWeek[day] === label);
  const currentKey = daysOfWeekKeys[idx + 1] || 'Sunday'; // clé du premier jour de la semaine

  return DayOfWeek[currentKey];
}

export { dayjs };
